import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Container,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { appIcons, appImages } from "../../assets";
import UserAllAddressDialog from "../../components/allAddressDialog";
import AppLoader from "../../components/apploader";
import GlobalButton from "../../components/button";
import AddressCard from "../../components/cards/AddressCard";
import AppFooter from "../../components/footer";
import InputField from "../../components/InputField";
import Label from "../../components/label";
import NavBar from "../../components/NavBar";
import AppointmentBookingSkeleton from "../../components/skeletons/bookingSkeleton";
import { useSnackbar } from "../../lib/context/snackbarContext";
import { CURRENCY } from "../../lib/variables";
import { createAppointment } from "../../services/appointmentFn";
import { getCompanyServiceDetail } from "../../services/companyService";
import { getAdminSetting } from "../../services/settingFn";
import { getWorkerDetail } from "../../services/workerFn";
import { convertMinutesToHours, decodeHtml } from "../../utils";
import AddEditAddressDialog from "../user/addressBook/AddEditAddressDialog";
import SelectedServiceCard from "./../../components/cards/selectedServiceCard";
import PaymentForm from "./PaymentForm";
import { t } from "i18next";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY);
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_TEST_API_KEY);

const BookAppointmentPage = () => {
  const navigate = useNavigate();
  const openAddressDialog = useRef(null);
  const openAddEditAddressDialog = useRef(null);
  const { showSnackbar } = useSnackbar();
  const { workerId, serviceId } = useParams();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [workerDetail, setWorkerDetail] = useState(null);
  const [serviceDetail, setServiceDetail] = useState(null);

  const [isLoading1, setIsLoading1] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [userInput, setUserInput] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [clientAnswers, setClientAnswers] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [totalDuration, setTotalDuration] = useState(0);
  const [platformFee, setPlatformFee] = useState(0);
  const [cleaningType, setCleaningType] = useState("regular");
  const [inputValues, setInputValues] = useState({
    address: "",
    notes: "",
  });

  const userData = useSelector((state) => state.userReducer.user);
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  const userAddress = useSelector(
    (state) => state.userAddressReducer.userAddress
  );

  useEffect(() => {
    fetchAdminSetting();
    if (workerId && serviceId) {
      fetchDetails(workerId, serviceId);
    }
  }, [workerId, serviceId]);

  const fetchDetails = async (workerId, subserviceId) => {
    try {
      setIsLoading(true);

      const workerResponse = await getWorkerDetail(workerId);
      if (workerResponse?.data.status !== "success") {
        showSnackbar(workerResponse.data.message, "error");
        return;
      }

      const workerData = workerResponse.data.data;
      setWorkerDetail(workerData);

      const serviceResponse = await getCompanyServiceDetail(
        workerData.companyId._id,
        subserviceId
      );
      if (serviceResponse?.data.status !== "success") {
        showSnackbar(serviceResponse.data.message, "error");
        return;
      }

      const serviceData = serviceResponse.data.data;
      setServiceDetail(serviceData);
      if (serviceData.serviceSubCategoryId.clientQuestionnaire?.length > 0) {
        setClientAnswers(
          serviceData.serviceSubCategoryId.clientQuestionnaire.map((q) => ({
            questionId: q._id,
            question: q.question,
            answer: "",
          }))
        );
      }
      if (serviceData?.serviceSubCategoryId?.type === "fixed") {
        setTotalDuration(serviceData?.serviceSubCategoryId?.duration?.fixed);
        setTotalPrice(parseInt(serviceData?.price?.fixed));
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdminSetting = async () => {
    try {
      const response = await getAdminSetting();
      if (response.data.status === "success") {
        const data = response.data.data;
        setPlatformFee(data.platformFee);
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const handleOptionChange = (e) => {
    const selectedValues = e.target.value;
    setSelectedOption(selectedValues);
    setTotalPrice(0);
    setTotalDuration(0);

    if (serviceDetail?.serviceSubCategoryId?.type === "interior+exterior") {
      if (selectedValues === "interior") {
        setTotalPrice(serviceDetail?.price?.interior || 0);
        setTotalDuration(
          serviceDetail?.serviceSubCategoryId?.duration?.interior
        );
      }
      if (selectedValues === "exterior") {
        setTotalPrice(serviceDetail?.price?.exterior || 0);
        setTotalDuration(
          serviceDetail?.serviceSubCategoryId?.duration?.exterior
        );
      }
      if (selectedValues === "both") {
        setTotalPrice(
          parseInt(serviceDetail?.price?.interior) +
            parseInt(serviceDetail?.price?.exterior) || 0
        );
        setTotalDuration(
          parseInt(serviceDetail?.serviceSubCategoryId?.duration?.exterior) +
            parseInt(serviceDetail?.serviceSubCategoryId?.duration?.interior)
        );
      }
    }
  };

  const handleInputChange = (e) => {
    let value = parseInt(e.target.value);
    setUserInput(value);

    if (serviceDetail?.serviceSubCategoryId?.type === "room+area") {
      if (selectedOption === "room" && serviceDetail?.price?.room) {
        setTotalPrice(value * serviceDetail.price.room);
        setTotalDuration(
          value * serviceDetail?.serviceSubCategoryId?.duration?.room
        );
      } else if (
        (selectedOption === "apartment" || selectedOption === "deluxe") &&
        serviceDetail?.price?.area
      ) {
        setTotalPrice(value * serviceDetail.price.area);
        setTotalDuration(
          value * serviceDetail?.serviceSubCategoryId?.duration?.area
        );
      }
    } else if (serviceDetail?.serviceSubCategoryId?.type === "area") {
      if (serviceDetail?.price?.area) {
        setTotalPrice(value * serviceDetail.price.area);
        setTotalDuration(
          value * serviceDetail?.serviceSubCategoryId?.duration?.area
        );
      }
    }
  };

  const handleAnswerChange = (questionId, value, question, index) => {
    setClientAnswers((prev) => {
      const updatedAnswers = [...prev];
      updatedAnswers[index] = { questionId, question, answer: value };
      return updatedAnswers;
    });
  };

  const handleChangeValues = (event) => {
    const { name, value } = event.target;
    setInputValues((pre) => ({ ...pre, [name]: value }));
  };

  const renderFinalPrice = () => {
    let px = totalPrice;

    if (
      serviceDetail?.mainServiceId?.name.en === "cleaning" &&
      cleaningType === "deep"
    ) {
      px = px * 2;
    }

    return isNaN(px) ? 0 : px;
  };

  let platformCharges = (renderFinalPrice() * Number(platformFee)) / 100;

  const handleBookAppointment = async (token) => {
    if (!selectedDate && !selectedTime) {
      return showSnackbar(t("pleaseSelectDateTime"), "error");
    }

    const startDateTime = new Date(selectedDate);
    const startTimeOnly = new Date(selectedTime.$d);

    if (isNaN(startDateTime.getTime()) || isNaN(startTimeOnly.getTime())) {
      return showSnackbar(t("invalidStartDateTimeFormat"), "error");
    }

    if (!userAddress) return showSnackbar(t("selectYourAddress"), "error");

    startDateTime.setHours(startTimeOnly.getHours());
    startDateTime.setMinutes(startTimeOnly.getMinutes());
    startDateTime.setSeconds(startTimeOnly.getSeconds());

    const endDateTime = new Date(
      startDateTime.getTime() + totalDuration * 60 * 1000
    );

    const selectedType = serviceDetail?.serviceSubCategoryId?.type;
    const mainType = serviceDetail?.mainServiceId?.name.en === "cleaning";

    let payload = {
      appointmentType: "online",
      companyId: serviceDetail?.companyId,
      mainServiceId: serviceDetail?.mainServiceId._id,
      serviceSubCategoryId: serviceDetail?.serviceSubCategoryId._id,
      companyServiceId: serviceDetail?._id,
      userId: userData?._id,
      workerId: workerDetail?._id,

      companyDetails: workerDetail?.companyId,
      mainServiceDetails: serviceDetail?.mainServiceId,
      serviceSubCategoryDetails: serviceDetail?.serviceSubCategoryId,
      companyServiceDetails: serviceDetail,
      workerDetails: workerDetail,

      userInputs: {
        selectedType,
        selectedOption: selectedOption || selectedType,
        userInput,
        ...(mainType && { cleaningType }),
      },
      start: new Date(startDateTime),
      end: new Date(endDateTime),
      totalCost: renderFinalPrice() + Math.ceil(platformCharges),
      platformFee: Number(Math.ceil(platformCharges)),
      platformPercentage: parseInt(platformFee),
      duration: totalDuration,
      percentage: serviceDetail?.serviceSubCategoryId?.percentage,
      companyPercentage: serviceDetail?.percentage,
      location: userAddress,
      notes: inputValues.notes,
      clientAnswers,
      tokenId: token.id,
    };

    try {
      setIsLoading1(true);
      const response = await createAppointment(payload);
      if (response.data.status === "success") {
        showSnackbar(response.data.message, "success");
        navigate("/dashboard/service");
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading1(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading1} />
      <NavBar />
      <Box
        height={{ xs: "60vh", sm: "100vh" }}
        display="flex"
        flexDirection="column"
        mt={8}
        position={"relative"}
        sx={{
          background: (theme) => theme.palette.gradient.background,
          // backgroundImage: `url(${appImages.educationCityDetailBg})`,
          backgroundImage: `url(${
            serviceDetail?.mainServiceId.name.en == "cleaning"
              ? appImages.cleaningBgImg
              : appImages.carServiceBgImg
          })`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* <Header /> */}

        <img
          src={appImages.helpPageWaveImg}
          width={"100%"}
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            left: 0,
            zIndex: 1,
          }}
        />
      </Box>

      <Box position={"absolute"} top={{ xs: 130, sm: 110 }} left={0} right={0}>
        <Container
          sx={{
            maxWidth: "1100px !important",
            position: "relative",
            zIndex: 999,
            flexGrow: 1,
            padding: "0px",
          }}
        >
          <GlobalButton
            handleClickBtn={() => navigate(-1)}
            variant={"outlined"}
            btnText={"goBack"}
            startIcon={<img src={appIcons.whiteArrowBackIcon} width={30} />}
            styleProps={{
              color: "text.white",
              borderColor: "text.white",
            }}
          />
          {/* Content Section */}
          <Box flexGrow={1} mt={1}>
            <Container sx={{ padding: "0px" }}>
              <Typography
                variant="heading_pr"
                component={"h1"}
                maxWidth={1200}
                margin={"auto"}
                textAlign={"center"}
                color={"text.white"}
                lineHeight={"normal"}
                mb={5}
              >
                {t("bookAppointmentDesc")}
              </Typography>
              {isLoading ? (
                <AppointmentBookingSkeleton />
              ) : (
                <>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {t("fillDetails")}
                    </Typography>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      Source:{" "}
                      <Typography color="text.coralRed" component={"span"}>
                        {t("onlineBookingService")}
                      </Typography>
                    </Typography>

                    <SelectedServiceCard serviceDetail={serviceDetail} />

                    {serviceDetail?.mainServiceId?.name === "cleaning" && (
                      <RadioGroup
                        sx={{ mt: 2 }}
                        row
                        value={cleaningType}
                        onChange={(e) => setCleaningType(e.target.value)}
                      >
                        <FormControlLabel
                          value="regular"
                          control={<Radio />}
                          label={t("regularCleaning")}
                        />
                        <FormControlLabel
                          value="deep"
                          control={<Radio />}
                          label={t("deepCleaning")}
                        />
                      </RadioGroup>
                    )}

                    {/* Handle "interior+exterior" service type */}
                    {serviceDetail?.serviceSubCategoryId?.type ===
                      "interior+exterior" && (
                      <>
                        <Typography variant="h6" fontWeight={600} my={2}>
                          Select Service Type
                        </Typography>
                        <RadioGroup
                          row
                          value={selectedOption}
                          onChange={handleOptionChange}
                        >
                          <FormControlLabel
                            value="interior"
                            control={<Radio />}
                            label={t("interior")}
                          />
                          <FormControlLabel
                            value="exterior"
                            control={<Radio />}
                            label={t("exterior")}
                          />
                          <FormControlLabel
                            value="both"
                            control={<Radio />}
                            label={t("interior_exterior")}
                          />
                        </RadioGroup>
                      </>
                    )}

                    {/* Handle "room+area" service type */}
                    {serviceDetail?.serviceSubCategoryId?.type ===
                      "room+area" && (
                      <>
                        <Typography variant="h6" fontWeight={600} my={2}>
                          {t("selectServiceType")}
                        </Typography>
                        <RadioGroup
                          row
                          value={selectedOption}
                          onChange={handleOptionChange}
                        >
                          <FormControlLabel
                            value="room"
                            control={<Radio />}
                            label={t("room")}
                          />
                          <FormControlLabel
                            value="apartment"
                            control={<Radio />}
                            label={t("apartment")}
                          />
                          <FormControlLabel
                            value="deluxe"
                            control={<Radio />}
                            label={t("deluxe")}
                          />
                        </RadioGroup>

                        {selectedOption === "room" && (
                          <>
                            <Typography variant="h6" fontWeight={600} my={2}>
                              {t("enterRoomsNumber")}
                            </Typography>
                            <InputField
                              type="number"
                              value={userInput}
                              onChange={handleInputChange}
                              placeholder={t("enterRoomsNumber")}
                            />
                          </>
                        )}

                        {(selectedOption === "apartment" ||
                          selectedOption === "deluxe") && (
                          <>
                            <Typography variant="h6" fontWeight={600} my={2}>
                              {t("enterArea")}
                            </Typography>
                            <InputField
                              type="number"
                              value={userInput}
                              onChange={handleInputChange}
                              placeholder={t("enterArea")}
                            />
                          </>
                        )}
                      </>
                    )}

                    {/* Handle "area" service type */}
                    {serviceDetail?.serviceSubCategoryId?.type === "area" && (
                      <>
                        <Typography variant="h6" fontWeight={600} my={2}>
                          {t("enterYourArea")}
                        </Typography>
                        <InputField
                          type="number"
                          value={userInput}
                          onChange={handleInputChange}
                          placeholder={t("enterYourArea")}
                        />
                      </>
                    )}

                    {serviceDetail?.serviceSubCategoryId?.clientQuestionnaire
                      ?.length > 0 && (
                      <>
                        <Typography variant="h6" fontWeight={600} my={2}>
                          {t("answerTheQuestions")}
                        </Typography>

                        {serviceDetail?.serviceSubCategoryId?.clientQuestionnaire?.map(
                          (question, index) => (
                            <Box key={question._id} mb={2}>
                              <Typography variant="body2" fontWeight={500}>
                                {index + 1}.{" "}
                                {question.question[currentLanguage]}
                              </Typography>
                              <InputField
                                type={question.type}
                                value={clientAnswers[index]?.answer || ""}
                                onChange={(e) =>
                                  handleAnswerChange(
                                    question._id,
                                    e.target.value,
                                    question.question,
                                    index
                                  )
                                }
                                placeholder={`${t("answerFor")} "${
                                  question.question[currentLanguage]
                                }"`}
                              />
                            </Box>
                          )
                        )}
                      </>
                    )}

                    <Typography variant="h6" fontWeight={600} my={2}>
                      {t("selectDateTime")}
                    </Typography>

                    <Grid container spacing={2} mt={2}>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <Label text={t("selectDate")} required />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={selectedDate}
                            disablePast
                            // minDate={dayjs().add(1, "day")}
                            onChange={(newValue) => setSelectedDate(newValue)}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-input": {
                                padding: "8.5px 14px",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <Label text={t("selectTime")} required />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            value={selectedTime}
                            onChange={(newValue) => setSelectedTime(newValue)}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-input": {
                                padding: "8.5px 14px",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      {/* <Grid item size={{ xs: 12 }}>
                        <Label
                          text={"Enter your address"}
                          required
                          htmlFor="appointment-address"
                        />
                        <InputField
                          id={"appointment-address"}
                          placeholder={"Enter your address"}
                          required
                          name={"address"}
                          value={inputValues.address}
                          onChange={handleChangeValues}
                        />
                      </Grid> */}
                      <Grid item size={{ xs: 12 }}>
                        <GlobalButton
                          variant={"outlined"}
                          btnText={
                            !userAddress ? t("addAddress") : t("editAddress")
                          }
                          startIcon={!userAddress ? <AddIcon /> : <EditIcon />}
                          handleClickBtn={() => {
                            openAddressDialog.current.openDialog();
                          }}
                          styleProps={{
                            width: "auto",
                            boxShadow: "none",
                            marginBottom: 1,
                          }}
                        />
                        {userAddress && (
                          <AddressCard
                            showButton={false}
                            cardData={userAddress}
                            // selectedValue={selectedAddress?._id}
                            // handleChange={handleChangeAddress}
                          />
                        )}
                      </Grid>

                      <Grid item size={{ xs: 12 }}>
                        <Label
                          text={t("noteMessage")}
                          required
                          htmlFor="appointment-note"
                        />
                        <InputField
                          id={"appointment-note"}
                          placeholder={t("enterNoteMessage")}
                          multiline
                          rows={4}
                          required
                          name={"notes"}
                          value={inputValues.notes}
                          onChange={handleChangeValues}
                        />
                      </Grid>
                    </Grid>

                    {/* Display Total Price */}
                    {/* <Typography variant="h6" fontWeight={600} mt={2}>
                      Platform Percentage : {platformFee}%
                    </Typography> */}
                    <Stack
                      direction={{ sm: "row" }}
                      alignItems={{ sm: "center" }}
                      justifyContent={{ sm: "space-between" }}
                    >
                      <Typography variant="h6" fontWeight={600}>
                        {t("totalServiceCharges")} :{" "}
                        {/* {totalPrice || serviceDetail?.price?.fixed || 0} EUR */}
                        {`${CURRENCY} ${renderFinalPrice()}`}
                      </Typography>
                      <Typography variant="h6" fontWeight={600} mt={2}>
                        {t("totalDuration")} :{" "}
                        {convertMinutesToHours(totalDuration) || "0 minute"}
                      </Typography>
                    </Stack>

                    <Typography variant="h6" fontWeight={600}>
                      {t("platformCharges")} : {CURRENCY}{" "}
                      {Math.ceil(platformCharges)}
                    </Typography>

                    <Typography variant="h6" fontWeight={600} mb={2}>
                      {t("totalPrice")}{" "}
                      <span
                        style={{
                          fontSize: "14px",
                          color: "grey",
                          fontWeight: 400,
                        }}
                      >
                        ({t("inclusiveVat")})
                      </span>{" "}
                      : {CURRENCY}{" "}
                      {parseInt(renderFinalPrice()) +
                        Math.ceil(platformCharges)}
                    </Typography>

                    <Elements stripe={stripePromise}>
                      <PaymentForm handlBooked={handleBookAppointment} />
                    </Elements>

                    {/* <Stack mt={2}>
                      <GlobalButton
                        btnText={"Book Appointment"}
                        handleClickBtn={handleBookAppointment}
                      />
                    </Stack> */}

                    {serviceDetail?.serviceSubCategoryId?.longDescription[
                      currentLanguage
                    ] && (
                      <>
                        <Typography variant="h6" fontWeight={600} my={2}>
                          {t("description")}
                        </Typography>

                        <div
                          dangerouslySetInnerHTML={{
                            __html: decodeHtml(
                              serviceDetail?.serviceSubCategoryId
                                ?.longDescription[currentLanguage]
                            ),
                          }}
                        ></div>
                      </>
                    )}
                  </Paper>
                </>
              )}
            </Container>
          </Box>
        </Container>
        <AppFooter bgcolor={true} />
      </Box>
      <AddEditAddressDialog ref={openAddEditAddressDialog} />
      <UserAllAddressDialog
        ref={openAddressDialog}
        openAddAddressDialog={() => {
          openAddEditAddressDialog.current.openDialog({
            type: "add",
            getData: () => {},
            data: null,
          });
        }}
      />
    </>
  );
};

export default BookAppointmentPage;
